import { useSelector } from 'react-redux'

import type { User } from 'types/user'

/** Information about remaining trial time and status. */
export type TrialLeft = {
  /** Human-readable message about trial status */
  message: string
  /** Whether the trial has expired */
  expired: boolean
  /** Number of days remaining (negative if expired) */
  period: number
}

// TODO: why is this still used ?
const isTrial = (user: User): boolean => {
  const plan_type = user.business?.private_metadata.plan_type
  // console.count('isTrial')
  return plan_type !== 'subscriber' && plan_type !== 'bank_draft_subscriber'
}

/**
 * Checks if a user's trial has expired.
 *
 * Determines if the trial period has ended based on trial dates and settings.
 * Returns false for non-trial users or when trial end check is skipped.
 *
 * @param user - User object containing business and trial information
 * @returns True if trial has expired and should show expired state
 *
 * @example
 * ```typescript
 * if (isTrialExpired(user)) {
 *   showTrialExpiredModal()
 * }
 * ```
 */
export function isTrialExpired(user: User): boolean {
  if (!user.business) return false
  const {
    private_metadata: { trial_period, skip_trial_end_check },
    stats: { trial_started_at, trial_ends_at },
  } = user.business

  const trialEnd = trial_ends_at || trial_started_at + (trial_period || 30)
  const trialEndDate = new Date(trialEnd * 1000)
  const currentDate = new Date()
  const show_trial_expired_popup =
    currentDate > trialEndDate && !skip_trial_end_check

  if (!isTrial(user)) {
    return false
  } else {
    if (show_trial_expired_popup) {
      return true
    } else {
      return false
    }
  }
}

/** Message formatting mode for trial notifications. */
export type TrialMessageMode = 'short' | 'long'

const SHORT_MESSAGE_MODE = 'short'

const messages = {
  default: [
    (messageMode: TrialMessageMode, _period?: number) =>
      messageMode === SHORT_MESSAGE_MODE
        ? 'Less than a day of trial left.'
        : 'You have less than a day on your trial.',

    (messageMode: undefined | TrialMessageMode, period: number) =>
      messageMode === SHORT_MESSAGE_MODE
        ? `${period} day${period > 1 ? 's' : ''} of trial left.`
        : `You have ${period} day${period > 1 ? 's' : ''} left on your trial.`,

    (messageMode: undefined | TrialMessageMode, _period?: number) =>
      messageMode === SHORT_MESSAGE_MODE
        ? 'Trial expired.'
        : 'Your trial has expired.',
  ],
  libraries: [
    (_messageMode: TrialMessageMode, _period?: number) =>
      'You have less than a day on your Catalogs trial.',

    (_messageMode: undefined | TrialMessageMode, period: number) =>
      `You have ${period} day${
        period > 1 ? 's' : ''
      } left on your Catalogs trial.`,

    (_messageMode: TrialMessageMode, _period?: number) =>
      'Your Catalogs trial has expired.',
  ],
} as const

/** Feature-specific trial message variants. */
export type TrialLeftFeatures = 'libraries' | 'default'

/**
 * Calculates trial status and generates appropriate message.
 *
 * Determines how many days are left in a trial and generates contextual
 * messages based on the time remaining and feature being used.
 *
 * @param trialEndBase - Trial end date as timestamp (seconds or milliseconds) or date string
 * @param messageMode - Format mode for the message ('short' or 'long')
 * @param feature - Feature context for specialized messaging
 * @returns Object containing trial status and formatted message
 *
 * @example
 * ```typescript
 * const trialInfo = getTrialLeft(1640995200, 'short', 'default')
 * // { message: '5 days of trial left.', expired: false, period: 5 }
 *
 * const expiredTrial = getTrialLeft(1640908800, 'long', 'libraries')
 * // { message: 'Your Catalogs trial has expired.', expired: true, period: -1 }
 * ```
 */
export function getTrialLeft(
  trialEndBase: number | string,
  messageMode?: TrialMessageMode,
  feature?: TrialLeftFeatures,
): TrialLeft {
  let message = ''
  let trialEnd: number

  if (typeof trialEndBase === 'number') {
    trialEnd = trialEndBase * 1000
  } else {
    // Handle string input - could be milliseconds as string or ISO date string
    const parsed = parseInt(trialEndBase, 10)
    if (!isNaN(parsed) && parsed.toString() === trialEndBase) {
      // It's a number string (milliseconds)
      trialEnd = parsed
    } else {
      // It's a date string
      trialEnd = new Date(trialEndBase).getTime()
    }
  }

  const currentDate = Date.now()
  const msInOneDay = 24 * 3600 * 1000
  const period = Math.floor((trialEnd - currentDate) / msInOneDay)
  const expired = period < 0

  const messageConfig = messages[feature || 'default']

  if (period === 0) {
    message = messageConfig[0](messageMode)
  } else if (period >= 1) {
    message = messageConfig[1](messageMode, period)
  } else {
    message = messageConfig[2](messageMode, period)
  }

  return {
    message,
    expired,
    period,
  }
}

/**
 * React hook that returns trial status for the current user.
 *
 * Uses Redux selector to get trial end date from user state and
 * calculates trial status information.
 *
 * @returns Trial status object with message, expiry status, and remaining days
 *
 * @example
 * ```typescript
 * function TrialBanner() {
 *   const { message, expired, period } = useTrialLeft()
 *
 *   if (expired) {
 *     return <ErrorBanner>{message}</ErrorBanner>
 *   }
 *
 *   if (period <= 3) {
 *     return <WarningBanner>{message}</WarningBanner>
 *   }
 *
 *   return null
 * }
 * ```
 */
export function useTrialLeft(): TrialLeft {
  const trialEnd = useSelector(
    // @ts-expect-error state is untyped
    state => state.user.business?.stats.trial_ends_at,
  )
  return getTrialLeft(trialEnd)
}
